import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import { financeApi, DailyRevenueEntryResponse } from '../../api/financeApi';
import {
  chartPalette,
  gridProps,
  axisTick,
  quietAxisProps,
  tooltipContentStyle,
  barRadius,
  MAX_BAR_SIZE,
  SERIES_FILL_OPACITY,
  activeBarProps,
  legendProps,
} from './chartTheme';

type Granularity = 'DAY' | 'WEEK' | 'MONTH';

interface Props {
  dateFrom: string;
  dateTo: string;
}

const PALETTE = chartPalette;

function bucketKey(dateIso: string, g: Granularity): string {
  const d = new Date(dateIso);
  if (g === 'DAY') return dateIso;
  if (g === 'MONTH') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return monday.toISOString().slice(0, 10);
}

function bucketLabel(key: string, g: Granularity): string {
  if (g === 'MONTH') {
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  return new Date(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const ChannelStackedBarChart: React.FC<Props> = ({ dateFrom, dateTo }) => {
  const { theme } = useTheme();
  const [granularity, setGranularity] = useState<Granularity>('DAY');
  const [entries, setEntries] = useState<DailyRevenueEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    financeApi
      .getDailyRevenueRange(dateFrom, dateTo)
      .then((res) => {
        if (cancelled) return;
        setEntries(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load channel data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  const { rows, channels } = useMemo(() => {
    const channelSet = new Set<string>();
    const buckets = new Map<string, Record<string, number | string>>();
    entries.forEach((e) => {
      if (!e.date) return;
      const key = bucketKey(e.date, granularity);
      const cName = e.channelName || `Channel ${e.channelId}`;
      channelSet.add(cName);
      const row = buckets.get(key) ?? { __key: key, __label: bucketLabel(key, granularity) };
      row[cName] = ((row[cName] as number) || 0) + (e.grossAmount || 0);
      buckets.set(key, row);
    });
    return {
      rows: Array.from(buckets.values()).sort((a, b) => String(a.__key).localeCompare(String(b.__key))),
      channels: Array.from(channelSet).sort(),
    };
  }, [entries, granularity]);

  if (loading) {
    return (
      <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
        Loading…
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.danger }}>
        {error}
      </Box>
    );
  }
  if (rows.length === 0) {
    return (
      <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
        No channel revenue in this range
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
        {(['DAY', 'WEEK', 'MONTH'] as Granularity[]).map((g) => {
          const active = granularity === g;
          return (
            <Chip
              key={g}
              label={g === 'DAY' ? 'Daily' : g === 'WEEK' ? 'Weekly' : 'Monthly'}
              onClick={() => setGranularity(g)}
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 28,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: active ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                color: active ? theme.primary : theme.gray?.[500] || '#666',
                border: active ? `1.5px solid ${theme.primary}` : '1px solid transparent',
                '&:hover': { backgroundColor: active ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
              }}
            />
          );
        })}
      </Box>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={rows} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="__label" tick={axisTick} {...quietAxisProps} />
          <YAxis
            tick={axisTick}
            tickFormatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
            {...quietAxisProps}
          />
          <Tooltip
            allowEscapeViewBox={{ x: false, y: true }}
            wrapperStyle={{ zIndex: 30 }}
            formatter={(value: number) => formatCurrency(value, { showDecimals: false })}
            contentStyle={tooltipContentStyle}
            itemStyle={{ padding: '1px 0' }}
          />
          <Legend {...legendProps} />
          {channels.map((c, i) => (
            <Bar
              key={c}
              dataKey={c}
              stackId="rev"
              fill={PALETTE[i % PALETTE.length]}
              fillOpacity={SERIES_FILL_OPACITY}
              radius={i === channels.length - 1 ? barRadius : 0}
              maxBarSize={MAX_BAR_SIZE}
              {...activeBarProps}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default ChannelStackedBarChart;
